import { ReactElement, useEffect, useRef } from 'react';

import { InputFailureSvg, InputSuccessSvg } from '@cashier/cashier-assets';
import {
  Directions,
  InputTypes,
  PaymentFormField,
  ValidationStatuses,
} from '@cashier/cashier-model';
import {
  AmountConverter,
  eventBus,
  EventFactory,
} from '@cashier/cashier-shared';

import { cn, InputModeProvider, maskInput } from '../utils';

import defaultStyles from './defaultStyles.module.scss';
import inlineStyles from './inlineStyles.module.scss';

export interface InputGeneratorProps {
  field: PaymentFormField;
  validationStatus: ValidationStatuses;
  isDisabled: boolean;
  onChange: (value: string, isFocused: boolean) => void;
  hideLabel?: boolean;
  labelPosition: 'inline' | 'default';
  direction: Directions;
}

export const InputGenerator = ({
  field,
  validationStatus,
  isDisabled,
  onChange,
  hideLabel,
  labelPosition,
  direction,
}: InputGeneratorProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { name, label, type, value, required, placeholder, params } = field;
  const { pattern, min, max, prefix } = params || {};

  const isDefaultLabelPosition = labelPosition === 'default';
  const isInputWithInlineLabelPosition = labelPosition === 'inline';

  const changeHandler = (): void => {
    if (!inputRef?.current) return;

    maskInput(inputRef, name, type);

    onChange(
      inputRef.current.value,
      document.activeElement === inputRef.current,
    );
  };

  useEffect(() => {
    if (!inputRef.current) return;

    if (value && typeof value === 'string') {
      // TODO: convert data without amount specific services
      inputRef.current.value = AmountConverter.toNonBreakingString(value);

      const event = new Event('input', {
        bubbles: true,
        cancelable: true,
      });

      inputRef.current.dispatchEvent(event);
    }
  }, [value, type, name, pattern, required, params]);

  useEffect(() => {
    if (!inputRef.current) return;

    if (!value) {
      inputRef.current.value = '';
    }
  }, [field, value]);

  const onFocusHandler = (): void => {
    if (!inputRef.current) return;

    onChange(inputRef.current.value, true);
    eventBus.emit(EventFactory.onFieldFocused(name));
  };

  const onBlurHandler = (): void => {
    if (!inputRef.current) return;

    onChange(inputRef.current.value, false);
  };

  const emptyPlaceholder = isDefaultLabelPosition ? '' : ' ';
  const inputPlaceholder = prefix ? '' : placeholder || emptyPlaceholder;

  const isError =
    document.activeElement !== inputRef.current &&
    validationStatus === ValidationStatuses.FAILED;

  const inputWrapperStyles = isDefaultLabelPosition
    ? cn({
        [defaultStyles.inputWrapper]: true,
        [defaultStyles.error]: isError,
        [defaultStyles.active]: !!value,
      })
    : cn({
        [inlineStyles.inputWrapper]: true,
        [inlineStyles.error]: isError,
        [inlineStyles.active]: Boolean(inputRef.current?.value),
      });

  const inputStyles = isDefaultLabelPosition
    ? defaultStyles.input
    : inlineStyles.input;

  const prefixStyles = isDefaultLabelPosition
    ? defaultStyles.prefix
    : inlineStyles.prefix;

  return (
    <div
      className={cn({
        [defaultStyles.inputHolder]: isDefaultLabelPosition,
        [inlineStyles.wrapper]: isInputWithInlineLabelPosition,
      })}
    >
      {!hideLabel && isDefaultLabelPosition && (
        <label
          htmlFor={name}
          className={cn([
            defaultStyles.inputLabel,
            defaultStyles.inputLabelCashier,
          ])}
          data-test={`${name}-input-label`}
        >
          {label}
        </label>
      )}

      <div className={inputWrapperStyles} dir={direction}>
        {prefix && <div className={prefixStyles}>{prefix}</div>}

        <input
          data-test={`${name}-input`}
          id={name}
          type={type || InputTypes.Text}
          name={name}
          placeholder={inputPlaceholder}
          ref={inputRef}
          onChange={changeHandler}
          disabled={isDisabled}
          min={min || undefined}
          max={max || undefined}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          inputMode={InputModeProvider.byInputType(type)}
          className={inputStyles}
        />
      </div>

      {isInputWithInlineLabelPosition && (
        <>
          <label
            className={cn({
              [inlineStyles.label]: true,
              [inlineStyles.dynamicLabel]: !!prefix || !!placeholder,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {label}
          </label>
          <div
            className={cn({
              [inlineStyles.icon]: true,
              [inlineStyles.rtl]: direction === Directions.Rtl,
            })}
          >
            {validationStatus === ValidationStatuses.SUCCESS && (
              <div className={inlineStyles.success}>
                <InputSuccessSvg />
              </div>
            )}
            {isError && (
              <div className={inlineStyles.error}>
                <InputFailureSvg />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
